import { useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { WakzyLogo } from "./WakzyLogo";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";

const SESSION_KEY = "wakzy_kiosk_unlocked";
// Pin lives in env (REACT_APP_KIOSK_PIN), falls back to 1234 for first-run convenience.
// Change in /app/frontend/.env to lock down (and rebuild/redeploy).
const KIOSK_PIN = process.env.REACT_APP_KIOSK_PIN || "1234";

export function isKioskUnlocked() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function lockKiosk() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* sessionStorage unavailable */ }
}

export function unlockKiosk() {
  try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* sessionStorage unavailable */ }
}

/**
 * Wrap any kiosk route. If the device hasn't been unlocked this session,
 * present a PIN entry screen instead. Staff enters the PIN once per device
 * (or after browser restart). Customers walking up to the tablet see only the
 * kiosk flow — they can't navigate back to /admin or the public site without
 * the PIN.
 */
export function KioskGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => isKioskUnlocked());
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (loc.search.includes("lock=1")) {
      lockKiosk();
      setUnlocked(false);
      nav("/kiosk-launch", { replace: true });
    }
  }, [loc, nav]);

  if (unlocked) return children ?? <Outlet />;

  const submit = (e) => {
    e.preventDefault();
    if (pin === KIOSK_PIN) {
      unlockKiosk();
      setUnlocked(true);
      setError("");
      // Redirect into the kiosk landing
      if (loc.pathname === "/kiosk-launch") nav("/kiosk", { replace: true });
    } else {
      setError("Incorrect PIN");
      setPin("");
      setAttempts((a) => a + 1);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: "var(--wakzy-bg)" }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <WakzyLogo className="h-16 mx-auto mb-6" asLink={false} />
          <div className="brand-divider max-w-xs mx-auto mb-4">
            <span className="dot" />
            <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Staff only</span>
            <span className="dot" />
          </div>
          <h1 className="font-heading text-4xl chrome-text inline-block">Tablet Kiosk</h1>
          <p className="text-slate-400 text-sm mt-3">
            This device is locked. Enter the kiosk PIN to begin a customer session.
          </p>
        </div>

        <form onSubmit={submit} className="surface p-7 rounded-md" data-testid="kiosk-pin-form">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#D4AF37]" />
            </div>
          </div>
          <label className="block label-eyebrow text-center mb-3" style={{ color: "var(--wakzy-gold)" }}>Kiosk PIN</label>
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => { setPin(e.target.value); setError(""); }}
            placeholder="••••"
            maxLength={12}
            className="w-full bg-[#04060d] border border-white/10 rounded-sm h-14 text-center text-2xl tracking-[0.5em] font-mono text-white focus:border-[#D4AF37] focus:outline-none"
            data-testid="kiosk-pin-input"
          />
          {error && (
            <div className="mt-3 text-center text-sm text-red-400" data-testid="kiosk-pin-error">
              {error}{attempts >= 3 ? " — ask a manager to reset" : ""}
            </div>
          )}
          <Button
            type="submit"
            disabled={!pin}
            className="btn-cta rounded-sm h-12 w-full mt-5"
            data-testid="kiosk-pin-submit"
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Unlock Kiosk
          </Button>
        </form>

        <div className="text-center mt-6">
          <button
            onClick={() => nav("/")}
            className="text-xs text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
            data-testid="kiosk-back-public"
          >
            <ArrowLeft className="w-3 h-3" /> Back to wakzyautokiosk.com
          </button>
        </div>
      </div>
    </div>
  );
}
